/**
 * Interactive map location picker — search an address, use current GPS
 * location, or tap/drag a pin directly on the map. Free OpenStreetMap tiles
 * (CARTO dark basemap to match the app theme) + Nominatim geocoding (via
 * backend /geocode proxy), no API key needed.
 */
import React, { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./location-picker.css";
import { Search, MapPin, Loader2, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

// Brand-colored pin (matches --primary) instead of Leaflet's stock blue marker.
const pinIcon = L.divIcon({
  className: "cravve-pin",
  html: `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.716 23.284 0 15 0z" fill="hsl(var(--primary))"/>
    <circle cx="15" cy="15" r="5.5" fill="hsl(var(--primary-foreground))"/>
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});

const DEFAULT_CENTER = { latitude: 7.9465, longitude: -1.0232 }; // roughly central Ghana
const DEFAULT_ZOOM = 6;
const SELECTED_ZOOM = 15;

function FlyToPosition({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo([position.latitude, position.longitude], SELECTED_ZOOM, { duration: 0.8 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.latitude, position?.longitude]);
  return null;
}

function ClickToPlace({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
}

export function LocationPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [w3w, setW3w] = useState(null);

  // Whenever the picked point changes (GPS, search, map click, pin drag),
  // look up its what3words address for display. Purely cosmetic — silently
  // stays null if what3words isn't configured or the lookup fails.
  useEffect(() => {
    if (!value) {
      setW3w(null);
      return;
    }
    let cancelled = false;
    api
      .get("/geocode/w3w-for", { params: { lat: value.latitude, lon: value.longitude } })
      .then(({ data }) => {
        if (!cancelled) setW3w(data.words || null);
      })
      .catch(() => {
        if (!cancelled) setW3w(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value?.latitude, value?.longitude]);

  const locateMe = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setErrorMsg("Location isn't supported by your browser — search or tap the map instead.");
      return;
    }
    setLocating(true);
    setErrorMsg("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        setErrorMsg("Couldn't get your location — search or tap the map instead.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort auto-locate on first mount. Silent on failure — the map,
  // search, and manual pin placement all still work without it.
  useEffect(() => {
    if (!value) locateMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim() || searching) return;
    setSearching(true);
    setErrorMsg("");
    try {
      const { data } = await api.get("/geocode/search", { params: { q: query.trim() } });
      onChange({ latitude: data.latitude, longitude: data.longitude });
    } catch (err) {
      setErrorMsg("Couldn't find that location. Try a more specific address, or tap the map.");
    } finally {
      setSearching(false);
    }
  };

  const center = value || DEFAULT_CENTER;

  return (
    <div className="space-y-2.5">
      {/* A nested <form> inside the page's own form is invalid HTML and risks a
          double-submit, so this is a plain div — Enter-to-search is wired via onKeyDown. */}
      <div className="flex items-stretch gap-2 rounded-full border bg-card shadow-sm pl-4 pr-1.5 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
        <Search className="w-4 h-4 self-center text-muted-foreground shrink-0" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") runSearch(e);
          }}
          placeholder="Search an address or ///what3words..."
          data-testid="location-search-input"
          className="border-0 shadow-none h-8 px-2 focus-visible:ring-0 bg-transparent"
        />
        {query && (
          <Button
            type="button" size="sm" onClick={runSearch} disabled={searching}
            className="h-8 rounded-full px-3 shrink-0"
            data-testid="location-search-btn"
          >
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Search"}
          </Button>
        )}
        <Button
          type="button" variant="ghost" size="icon" onClick={locateMe} disabled={locating}
          title="Use my current location" data-testid="location-current-btn"
          className="h-8 w-8 rounded-full shrink-0 text-primary hover:text-primary hover:bg-primary/10"
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
        </Button>
      </div>

      {errorMsg && <p className="text-xs text-destructive px-1">{errorMsg}</p>}

      <div className="cravve-map rounded-2xl overflow-hidden border shadow-elegant h-72" data-testid="location-map">
        <MapContainer
          center={[center.latitude, center.longitude]}
          zoom={value ? SELECTED_ZOOM : DEFAULT_ZOOM}
          scrollWheelZoom
          zoomControl={false}
          style={{ height: "100%", width: "100%", background: "hsl(var(--muted))" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={19}
          />
          <ClickToPlace onPick={onChange} />
          <FlyToPosition position={value} />
          <ZoomControlBottomRight />
          {value && (
            <Marker
              position={[value.latitude, value.longitude]}
              icon={pinIcon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  onChange({ latitude: lat, longitude: lng });
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-1 px-1">
        <MapPin className="w-3 h-3 shrink-0" />
        {value
          ? "Drag the pin or tap the map to fine-tune your location."
          : "Search, tap the locate button, or tap the map to set your delivery location."}
      </p>
      {w3w && (
        <p className="text-xs px-1 flex items-center gap-1.5" data-testid="location-w3w">
          <span className="text-muted-foreground">what3words:</span>
          <span className="font-mono font-medium text-primary">///{w3w}</span>
        </p>
      )}
    </div>
  );
}

// Moves Leaflet's zoom control to the bottom-right so it doesn't collide with
// the app's own overlaid UI, and lets it pick up the themed styles in
// location-picker.css (which target it via the .cravve-map ancestor class).
function ZoomControlBottomRight() {
  const map = useMap();
  useEffect(() => {
    const control = L.control.zoom({ position: "bottomright" });
    control.addTo(map);
    return () => control.remove();
  }, [map]);
  return null;
}
